import { webchatApi } from '../api';
import { isPushSupported, urlBase64ToUint8Array } from './pushSupport';
import { registerServiceWorker } from './registerServiceWorker';

export async function fetchPushConfig() {
  const response = await webchatApi.get('/notifications/web-push/config/');
  return response.data;
}

export async function fetchPushStatus(organizationId, channelName) {
  const response = await webchatApi.get('/notifications/web-push/status/', {
    params: {
      organization_id: organizationId,
      channel_name: channelName,
    },
  });
  return response.data;
}

export async function disablePushReminders(organizationId, channelName) {
  const response = await webchatApi.post('/notifications/web-push/disable/', {
    organization_id: Number(organizationId),
    channel_name: channelName,
  });
  return response.data;
}

export async function requestTestPush(organizationId, channelName) {
  const response = await webchatApi.post('/notifications/web-push/test/', {
    organization_id: Number(organizationId),
    channel_name: channelName,
  });
  return response.data;
}

async function ensureRegistration() {
  return registerServiceWorker();
}

async function persistSubscription(organizationId, channelName, subscription) {
  const json = subscription.toJSON();
  const keys = json.keys || {};
  if (!json.endpoint || !keys.p256dh || !keys.auth) {
    throw new Error('Browser returned an incomplete push subscription.');
  }

  const response = await webchatApi.post('/notifications/web-push/subscribe/', {
    endpoint: json.endpoint,
    p256dh: keys.p256dh,
    auth_key: keys.auth,
    organization_id: Number(organizationId),
    channel_name: channelName,
    user_agent: window.navigator.userAgent || '',
  });

  return response.data;
}

async function createBrowserSubscription(registration, publicKey) {
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
}

export async function subscribeToPushReminders(
  organizationId,
  channelName,
  { recreate = true } = {}
) {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported in this browser.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  const config = await fetchPushConfig();
  if (!config?.public_key) {
    throw new Error('Push configuration is unavailable.');
  }

  const registration = await ensureRegistration();
  if (!registration) {
    throw new Error('Service worker registration failed.');
  }

  let subscription = await registration.pushManager.getSubscription();
  if (recreate && subscription) {
    try {
      await subscription.unsubscribe();
    } catch (error) {
      console.warn('Failed to clear previous push subscription', error);
    }
    subscription = null;
  }

  if (!subscription) {
    subscription = await createBrowserSubscription(registration, config.public_key);
  }

  return persistSubscription(organizationId, channelName, subscription);
}

export async function restorePushReminders(organizationId, channelName) {
  if (!isPushSupported()) {
    return { enabled: false, restored: false };
  }

  let status = { enabled: false };
  try {
    status = await fetchPushStatus(organizationId, channelName);
  } catch (error) {
    console.warn('Unable to load push reminder status', error);
    return { enabled: false, restored: false, error };
  }

  const enabled = Boolean(status?.enabled);
  if (!enabled) {
    return { enabled: false, restored: false };
  }

  if (Notification.permission !== 'granted') {
    return { enabled: true, restored: false };
  }

  const registration = await ensureRegistration();
  if (!registration) {
    return { enabled: true, restored: false };
  }

  let subscription = await registration.pushManager.getSubscription();

  if (!subscription) {
    try {
      const config = await fetchPushConfig();
      if (!config?.public_key) {
        return { enabled: true, restored: false };
      }
      subscription = await createBrowserSubscription(registration, config.public_key);
      await persistSubscription(organizationId, channelName, subscription);
      return { enabled: true, restored: true };
    } catch (error) {
      console.warn('Unable to recreate push subscription', error);
      return { enabled: true, restored: false, error };
    }
  }

  try {
    await persistSubscription(organizationId, channelName, subscription);
    return { enabled: true, restored: true };
  } catch (error) {
    console.warn('Unable to refresh push subscription', error);
    return { enabled: true, restored: false, error };
  }
}
